import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Container from "../components/Container";
import SectionTitle from "../components/Home/SectionTitle";

const isExternalLink = (url) => /^(https?:|mailto:|tel:)/i.test(url);

const defaultContent = {
  hero: {
    eyebrow: "مكتبة روحية",
    title: "الفيديوهات والصلوات",
    description: "صفحة تجمع مواد روحية وروابط داخلية مع نصوص صلوات عربية.",
  },
  sections: {
    featuredVideosTitle: "مواد مختارة",
    featuredVideosCountLabel: "عدد المواد",
    prayersTitle: "الصلوات",
    supportEyebrow: "رعاية روحية",
  },
  featuredVideos: [],
  prayers: [],
  support: {
    title: "هل تريد إرسال طلب صلاة؟",
    description: "يمكنك التوجّه إلى صفحة التواصل لطلب الصلاة أو إرسال استفسار.",
    buttonLabel: "فتح صفحة التواصل",
    buttonUrl: "/contact",
  },
};

function ActionLink({ url, label, className, dark = false }) {
  if (isExternalLink(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {label}
      </a>
    );
  }

  return (
    <Link to={url} className={className}>
      {label}
    </Link>
  );
}

export default function VideoPrayers() {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    fetch("/data/video-prayers.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `تعذّر تحميل محتوى الفيديوهات والصلوات: ${response.status}`
          );
        }

        return response.json();
      })
      .then((json) => {
        setContent({
          ...defaultContent,
          ...json,
          hero: {
            ...defaultContent.hero,
            ...json.hero,
          },
          sections: {
            ...defaultContent.sections,
            ...json.sections,
          },
          featuredVideos: json.featuredVideos ?? defaultContent.featuredVideos,
          prayers: json.prayers ?? defaultContent.prayers,
          support: {
            ...defaultContent.support,
            ...json.support,
          },
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  return (
    <main className="pb-20">
      <section className="pt-10">
        <Container>
          <div className="rounded-[36px] bg-[linear-gradient(135deg,#f4ede2_0%,#fff8ef_58%,#ddd4c1_100%)] px-6 py-12 text-right md:px-10">
            <p className="text-sm font-bold text-[#8a7355]">
              {content.hero.eyebrow}
            </p>
            <SectionTitle
              title={content.hero.title}
              classes="mt-3 max-w-3xl text-[#3e352b]"
            />
            <p className="mt-4 max-w-3xl text-[17px] leading-8 text-[#5f5549]">
              {content.hero.description}
            </p>
          </div>
        </Container>
      </section>

      <section className="pt-10">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-[32px] border border-[#eadfce] bg-white p-6 shadow-[0_18px_60px_rgba(76,58,37,0.08)]">
              <div className="flex items-center justify-between gap-4 border-b border-[#efe6d7] pb-4">
                <h2 className="text-[24px] font-bold text-[#3e352b]">
                  {content.sections.featuredVideosTitle}
                </h2>
                <span className="rounded-full bg-[#f2e7d6] px-4 py-2 text-xs font-bold text-[#8a7355]">
                  {content.sections.featuredVideosCountLabel}:{" "}
                  {content.featuredVideos.length}
                </span>
              </div>

              <div className="mt-6 grid gap-4">
                {content.featuredVideos.map((video) => (
                  <article
                    key={video.title}
                    className="rounded-[24px] bg-[#faf6ef] p-5 text-right"
                  >
                    <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-[#8a7355]">
                      <span>{video.speaker}</span>
                      <span>{video.duration}</span>
                    </div>
                    <h3 className="mt-3 text-[22px] font-bold text-[#2f281f]">
                      {video.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-7 text-[#61584b]">
                      {video.description}
                    </p>
                    <ActionLink
                      url={video.url}
                      label={video.ctaLabel}
                      className="mt-5 inline-flex rounded-full bg-[#8a7355] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#6f5c43]"
                    />
                  </article>
                ))}
              </div>
            </div>

            <div className="rounded-[32px] border border-[#eadfce] bg-white p-6 shadow-[0_18px_60px_rgba(76,58,37,0.08)]">
              <div className="border-b border-[#efe6d7] pb-4">
                <h2 className="text-[24px] font-bold text-[#3e352b]">
                  {content.sections.prayersTitle}
                </h2>
              </div>

              <div className="mt-6 grid gap-4">
                {content.prayers.map((prayer) => (
                  <article
                    key={prayer.title}
                    className="rounded-[24px] border border-[#efe6d7] p-5 text-right"
                  >
                    <h3 className="text-[20px] font-bold text-[#2f281f]">
                      {prayer.title}
                    </h3>
                    <p className="mt-2 text-sm font-medium text-[#8a7355]">
                      {prayer.excerpt}
                    </p>
                    <p className="mt-4 whitespace-pre-line text-[15px] leading-7 text-[#61584b]">
                      {prayer.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="pt-10">
        <Container>
          <div className="rounded-[32px] bg-[#212121] px-6 py-10 text-right text-white md:px-10">
            <p className="text-sm font-bold text-[#d9cab3]">
              {content.sections.supportEyebrow}
            </p>
            <h2 className="mt-3 text-[30px] font-bold">
              {content.support.title}
            </h2>
            <p className="mt-4 max-w-2xl text-[16px] leading-7 text-[#ddd5cb]">
              {content.support.description}
            </p>
            <ActionLink
              url={content.support.buttonUrl}
              label={content.support.buttonLabel}
              className="mt-6 inline-flex rounded-full bg-[#d9cab3] px-5 py-3 text-sm font-bold text-[#2f281f] transition-colors hover:bg-white"
            />
          </div>
        </Container>
      </section>
    </main>
  );
}
